import type {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react'
import { memo } from 'react'
import styles from './HourlyDataTable.module.less'
import type { MouseEventLike } from './HourlyDataTable.types'
import { HourlyInputCellFaultDisclosure } from './HourlyInputCellFaultDisclosure'
import { useHourlyInputCellFaultDisclosure } from './useHourlyInputCellFaultDisclosure'

type HourlyInputCellBaseProps = {
  /** Opaque key stored as data-cell-key; read by useTableCellSelection on drag */
  cellKey: string
  value: string
  isSelected: boolean
  isInvalid: boolean
  /** Adds left-side group border */
  isGroupStart: boolean
  /** Adds right-side group border */
  isGroupEnd: boolean
  ariaLabel: string
  /** Extra data-* attributes to spread onto the <td> */
  tdDataAttributes?: Record<string, string>
  onValueChange: (value: string) => void
  onFocus: () => void
  onBlur: () => void
  onKeyDown: (ev: ReactKeyboardEvent) => void
  onMouseDown: (ev: MouseEventLike) => void
  onMouseOver: () => void
  isReadOnly?: boolean
  isUnsaved?: boolean
  faultMessage?: string
}

export type HourlyInputCellProps = HourlyInputCellBaseProps &
  (
    | {
        multiline?: false
        registerRef: (element: HTMLInputElement | null) => void
        leadingAdornment?: ReactNode
      }
    | { multiline: true; registerRef: (element: HTMLTextAreaElement | null) => void; leadingAdornment?: never }
  )

export const HourlyInputCell = memo(function HourlyInputCell(props: HourlyInputCellProps): ReactNode {
  const {
    cellKey,
    value,
    isSelected,
    isInvalid,
    isGroupStart,
    isGroupEnd,
    ariaLabel,
    tdDataAttributes,
    onValueChange,
    onFocus,
    onBlur,
    onKeyDown,
    onMouseDown,
    onMouseOver,
    isReadOnly = false,
    isUnsaved = false,
    faultMessage,
    leadingAdornment,
  } = props

  const faultDisclosure = useHourlyInputCellFaultDisclosure()
  const hasFault = faultMessage !== undefined

  const tdClassName = [
    styles.HourlyDataTable_BidTableInputCell,
    isGroupStart ? styles.HourlyDataTable_BidTableGroupStartCell : '',
    isGroupEnd ? styles.HourlyDataTable_BidTableGroupEndCell : '',
    isSelected ? styles.HourlyDataTable_BidTableInputCellSelected : '',
    isInvalid ? styles.HourlyDataTable_BidTableInputCellInvalid : '',
    isUnsaved ? styles.HourlyDataTable_BidTableInputCellUnsaved : '',
    leadingAdornment ? styles.HourlyDataTable_BidTableInputCellAdorned : '',
  ]
    .filter(Boolean)
    .join(' ')

  const fieldProps = {
    value,
    'aria-label': ariaLabel,
    'aria-describedby': hasFault ? faultDisclosure.descriptionId : undefined,
    readOnly: isReadOnly,
    onChange: isReadOnly
      ? undefined
      : (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onValueChange(event.target.value),
    onFocus: () => {
      onFocus()
      faultDisclosure.handleFieldFocus()
    },
    onBlur: (ev: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (props.multiline) {
        ev.currentTarget.scrollTop = 0
      }
      onBlur()
      faultDisclosure.handleFieldBlur()
    },
    onKeyDown: isReadOnly ? undefined : onKeyDown,
    style: { userSelect: 'none' as const },
    onMouseDown: (ev: ReactMouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onMouseDown(ev)
      ev.stopPropagation()
    },
    onMouseOver,
    onDoubleClick: (ev: ReactMouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      ev.currentTarget.select()
    },
  }

  return (
    <td
      ref={hasFault ? faultDisclosure.registerAnchor : undefined}
      className={tdClassName}
      data-cell-key={cellKey}
      {...tdDataAttributes}
      onMouseDown={(ev) => onMouseDown(ev)}
      onMouseOver={onMouseOver}
      onFocus={onMouseOver}
      onMouseEnter={faultDisclosure.handleCellMouseEnter}
      onMouseLeave={faultDisclosure.handleCellMouseLeave}
    >
      {leadingAdornment ? (
        <div className={styles.HourlyDataTable_BidTableInputCellAdornment}>{leadingAdornment}</div>
      ) : null}
      {props.multiline ? (
        <textarea rows={2} ref={props.registerRef} {...fieldProps} />
      ) : (
        <input type="text" inputMode="decimal" ref={props.registerRef} {...fieldProps} />
      )}
      {hasFault && (
        <HourlyInputCellFaultDisclosure
          descriptionId={faultDisclosure.descriptionId}
          message={faultMessage}
          anchorElement={faultDisclosure.anchorElement}
          isOpen={faultDisclosure.isOpen}
        />
      )}
    </td>
  )
})
